import { useState, useEffect, useMemo } from "react";
import { Box, Alert, CircularProgress, Button, Divider } from "@mui/material";

import { FieldControl } from "../../../shared/components";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useGetMaterialReqDetails } from "../hooks/useGetMaterialReqDetails";

import { useCreateMaterialRequest, useGetActivity, useUpdateMaterialRequest } from "../mutations";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

interface AddRequestProps {
    projectId: string | undefined;
    requestId: string | null;
    isEdit: boolean;
    onSuccess: () => void;
    onClose: () => void;
    refetch: () => void;
}

export const AddRequest = ({ projectId, requestId, isEdit = false, onSuccess, onClose, refetch }: AddRequestProps) => {

    const { getFilterOptions } = useDisplayField([]);

    const phaseData = usePhase();
    const activityData = useGetActivity();

    const createMutation = useCreateMaterialRequest();
    const updateMutation = useUpdateMaterialRequest();

    const [requestData, setRequestData] = useState<{
        type: string | null,
        description: string | null,
        quantity: number,
        location: string | null,
        required_date: string | null,
        date_po_raised: string | null,
        po_number: string | null,
        activity_id: number | null,
        supplier: string | null,
        point_of_contact_info: string | null,
        owner_of_request: number | null,
        status: string | null,
        last_updated_comments: string | null,
        last_updated_date: string | null,
        order_placed_date: string | null,
        est_delivery_date: string | null,
        phase_id: number | null,
    }>({
        type: null,
        description: null,
        quantity: 0,
        location: null,
        required_date: null,
        activity_id: null,
        supplier: null,
        point_of_contact_info: null,
        owner_of_request: null,
        status: null,
        last_updated_comments: null,
        last_updated_date: null,
        order_placed_date: null,
        est_delivery_date: null,
        phase_id: null,
        po_number:  null,
        date_po_raised: null,
    });

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const handleChange = (key: string, value: any) => {
        setRequestData((prevData) => ({
            ...prevData,
            [key]: value,
        }));
        if (error) setError(null);
        // Clear validation error for this field when user makes changes
        if (validationErrors[key]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!requestData.type) {
            errors.type = "Type is required";
        }
        if (!requestData.description) {
            errors.description = "Description is required";
        }
        if (!requestData.quantity || requestData.quantity <= 0) {
            errors.quantity = "Quantity must be greater than 0";
        }
        if (!requestData.location) {
            errors.location = "Location is required";
        }
        if (!requestData.required_date) {
            errors.required_date = "Required date is required";
        }
        // if (!requestData.est_delivery_date) {
        //     errors.est_delivery_date = "Estimated delivery date is required";
        // }
        if (!requestData.supplier) {
            errors.supplier = "Supplier is required";
        }
        if (!requestData.phase_id) {
            errors.phase_id = "Phase is required";
        }
        if (!requestData.activity_id) {
            errors.activity_id = "Activity is required";
        }
        if (!requestData.status) {
            errors.status = "Status is required";
        }
        // if (!requestData.order_placed_date) {
        //     errors.order_placed_date = "Order placed date is required";
        // }
        // if (!requestData.point_of_contact_info) {
        //     errors.point_of_contact_info = "Point of contact info is required";
        // }
        if (!requestData.owner_of_request) {
            errors.owner_of_request = "Owner of request is required";
        }
        // if (!requestData.last_updated_comments) {
        //     errors.last_updated_comments = "Last updated comments are required";
        // }
        if (!requestData.last_updated_date) {
            errors.last_updated_date = "Last updated date is required";
        }

        //  if (!requestData.date_po_raised) {
        //     errors.date_po_raised = "Date PO raised is required";
        // }
        //  if (!requestData.po_number) {
        //     errors.po_number = "PO number is required";
        // }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setError("Please fill in all required fields correctly");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const payload = {
                "project_id": projectId,
                "phase_id": requestData.phase_id,
                "activity_id": requestData.activity_id,
                "type": requestData.type,
                "description": requestData.description,
                "location": requestData.location,
                "quantity": requestData.quantity,
                "order_placed_date": requestData.order_placed_date,
                "est_delivery_date": requestData.est_delivery_date,
                "supplier": requestData.supplier,
                "point_of_contact_info": requestData.point_of_contact_info,
                "owner_of_request": requestData.owner_of_request,
                "status": requestData.status,
                "required_date": requestData.required_date,
                "po_number": requestData.po_number,
                "date_po_raised": requestData.date_po_raised,
                "last_updated_comments": requestData.last_updated_comments,
                "last_updated_date": requestData.last_updated_date,
            };
            if (isEdit && requestId) {
                await updateMutation.mutateAsync({ id: requestId, payload });
                refetchDetails?.();
            } else {
                await createMutation.mutateAsync({ payload });
            }
            refetch?.();
            onSuccess?.();
            onClose?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} material request`;
            setError(errorMessage);
            console.error(`Failed to ${isEdit ? 'update' : 'create'} material request:`, err);
        } finally {
            setLoading(false);
        }
    };

    const { data, isFetching: isMaterialReqFetching, isPending: isMaterialReqPending, isError: isMaterialReqError, refetch: refetchDetails } = useGetMaterialReqDetails({ projectId, requestId });
    const { data: stakeHolderData, isLoading, } = useGetJeopardyStakeholders({ params: { project_id: projectId } });

    const userOptions = useMemo(() => {
        return stakeHolderData?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                value: user.user_id,
                label: user.user_name,
            }))
        ) || [];
    }, [stakeHolderData]);

    useEffect(() => {
        if (requestData.phase_id) {
            activityData.mutateAsync({ projectId: projectId, phaseId: String(requestData.phase_id) });
        };
    }, [requestData.phase_id]);

    useEffect(() => {
        setInitialDataLoaded(false);
        if (!isEdit) {
            setRequestData({
                type: null,
                description: null,
                quantity: 0,
                location: null,
                required_date: null,
                activity_id: null,
                supplier: null,
                point_of_contact_info: null,
                owner_of_request: null,
                status: null,
                last_updated_comments: null,
                last_updated_date: null,
                order_placed_date: null,
                est_delivery_date: null,
                po_number: null,
                date_po_raised: null,
                phase_id: null,

            });
        }
    }, [isEdit, requestId]);

    useEffect(() => {
        if (isEdit && requestId && data && !isMaterialReqFetching && !isMaterialReqPending && !isMaterialReqError && phaseData.data && !initialDataLoaded) {
            setRequestData({
                type: data.type,
                description: data.description,
                quantity: parseFloat(data.quantity) || 0,
                required_date: data.required_date,
                activity_id: Number(data.activity_id),
                supplier: data.supplier,
                point_of_contact_info: data.point_of_contact_info,
                location: data.location,
                owner_of_request: Number(data.owner_of_request),
                status: data.status,
                last_updated_comments: data.last_updated_comments,
                last_updated_date: data.last_updated_date,
                order_placed_date: data.order_placed_date,
                est_delivery_date: data.est_delivery_date,
                po_number: data.po_number,
                date_po_raised: data.date_po_raised,
                phase_id: Number(data.phase_id) || null,
            });
            setInitialDataLoaded(true);
        };
    }, [isEdit, requestId, data, isMaterialReqFetching, isMaterialReqPending, isMaterialReqError, phaseData.data, initialDataLoaded]);

    useEffect(() => {
        phaseData.mutateAsync(projectId);
    }, []);

    if (isLoading || (isEdit && (isMaterialReqFetching || isMaterialReqPending))) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>

            {error && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            <Box sx={{ px: 2, py: 2, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

                <FieldControl
                    keyName={"type"}
                    label={"Type"}
                    variant={"select"}
                    value={requestData.type}
                    options={getFilterOptions("MATERIAL_TYPE").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("type", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.type}
                    helperText={validationErrors.type}
                />

                <FieldControl
                    keyName={"description"}
                    label={"Description"}
                    variant={"input"}
                    value={requestData.description}
                    onChange={(_key: string, value: string) => handleChange("description", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.description}
                    helperText={validationErrors.description}
                />

                <FieldControl
                    keyName={"quantity"}
                    label={"Quantity"}
                    variant={"input"}
                    value={requestData.quantity.toString()}
                    onChange={(_key: string, value: string) => handleChange("quantity", parseFloat(value) || 0)}
                    disabled={loading}
                    required
                    error={!!validationErrors.quantity}
                    helperText={validationErrors.quantity}
                />
                 

                <FieldControl
                    keyName={"Location"}
                    label={"Location"}
                    variant={"input"}
                    value={requestData.location}
                    onChange={(_key: string, value: string) => handleChange("location", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.location}
                    helperText={validationErrors.location}
                />

                <FieldControl
                    keyName={"required_date"}
                    label={"Required Date"}
                    variant={"date"}
                    value={requestData.required_date}
                    onChange={(_key: string, value: string) => handleChange("required_date", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.required_date}
                    helperText={validationErrors.required_date}
                />

               
                <FieldControl
                    keyName={"est_delivery_date"}
                    label={"Estimated Delivery Date"}
                    variant={"date"}
                    value={requestData.est_delivery_date}
                    onChange={(_key: string, value: string) => handleChange("est_delivery_date", value)}
                    disabled={loading}
                    
                />

                <FieldControl
                    keyName={"supplier"}
                    label={"Supplier"}
                    variant={"select"}
                    value={requestData.supplier}
                    options={requestData.type ? getFilterOptions("MATERIAL_SUPPLIER").filter((m) => { return m.parent_key?.split(',').map((s: string) => s.trim()).includes(requestData.type) }).map((option) => ({ label: option.display_text, value: option.key })) : []}
                    onChange={(_key: string, value: string) => handleChange("supplier", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.supplier}
                    helperText={validationErrors.supplier}
                />

                <FieldControl
                    keyName={"po_number"}
                    label={"PO Number"}
                    variant={"input"}
                    value={requestData.po_number}
                    onChange={(_key: string, value: string) => handleChange("po_number", value)}
                    disabled={loading}
                 
                />
                 <FieldControl
                    keyName={"po_raised_date"}
                    label={"Date PO Requested"}
                    variant={"date"}
                    value={requestData.date_po_raised}
                    onChange={(_key: string, value: string) => handleChange("date_po_raised", value)}
                    disabled={loading}
                    
                />


                <FieldControl
                    keyName={"phase_id"}
                    label={"Phase"}
                    variant={"select"}
                    value={requestData.phase_id}
                    options={phaseData.data?.data.map((phase: { id: string; name: string }) => ({ label: phase.name, value: phase.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("phase_id", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.phase_id}
                    helperText={validationErrors.phase_id}
                />

                <FieldControl
                    keyName={"activity_id"}
                    label={"Activity"}
                    variant={"select"}
                    value={requestData.activity_id}
                    options={activityData.data?.data?.activities.map((activity: { id: string; name: string }) => ({ label: activity.name, value: activity.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("activity_id", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.activity_id}
                    helperText={validationErrors.activity_id}
                />

                <FieldControl
                    keyName={"status"}
                    label={"Status"}
                    variant={"select"}
                    value={requestData.status}
                    options={getFilterOptions("MATERIAL_STATUS").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("status", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.status}
                    helperText={validationErrors.status}
                />

                <FieldControl
                    keyName={"order_placed_date"}
                    label={"Date Order Placed"}
                    variant={"date"}
                    value={requestData.order_placed_date}
                    onChange={(_key: string, value: string) => handleChange("order_placed_date", value)}
                    disabled={loading}
                 />

                <FieldControl
                    keyName={"point_of_contact_info"}
                    label={"Point of Contact Info"}
                    variant={"input"}
                    value={requestData.point_of_contact_info}
                    onChange={(_key: string, value: string) => handleChange("point_of_contact_info", value)}
                    disabled={loading}
                   />

                <FieldControl
                    keyName={"owner_of_request"}
                    label={"Owner of Request"}
                    variant={"select"}
                    options={userOptions}
                    value={requestData.owner_of_request}
                    onChange={(_key: string, value: string) => handleChange("owner_of_request", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.owner_of_request}
                    helperText={validationErrors.owner_of_request}
                />

                <FieldControl
                    keyName={"last_updated_comments"}
                    label={"Comments on Last Update "}
                    variant={"input"}
                    value={requestData.last_updated_comments}
                    onChange={(_key: string, value: string) => handleChange("last_updated_comments", value)}
                    disabled={loading}
                   />

                <FieldControl
                    keyName={"last_updated_date"}
                    label={"Last Updated Date"}
                    variant={"date"}
                    value={requestData.last_updated_date}
                    onChange={(_key: string, value: string) => handleChange("last_updated_date", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.last_updated_date}
                    helperText={validationErrors.last_updated_date}
                />

            </Box>
            <Divider />
             <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 3,
                    py: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        minWidth: 150,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {loading && <CircularProgress size={20} />}
                    {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Request' : 'Create Request')}
                </Button>
            </Box>
            </Box>
    );

};